import type { TalkingAvatar } from '@components/TalkingAvatar';

// ── Koridor's browser client ─────────────────────────────────────────────
// The browser streams audio straight to Koridor over a WebSocket; our API only hands out a
// single-use ticket and stores the transcript Koridor pushes back. The client does the microphone,
// end-of-speech detection, playback and barge-in — the call screens only render state.

const KORIDOR_VOICE_CLIENT = 'https://api.koridor.ai/api/v1/voice/client.js';

export type KoridorCallState = 'connecting' | 'listening' | 'thinking' | 'speaking' | 'ended';
export type KoridorEndReason = 'hangup' | 'disconnected' | 'timeout' | 'refused' | 'error';

export interface KoridorCall {
  setMuted(muted: boolean): void;
  hangup(): Promise<void>;
}

interface KoridorVoiceApi {
  start(options: {
    ticket: string;
    socketUrl: string;
    onState?: (state: KoridorCallState) => void;
    onPartial?: (text: string) => void;
    onUserText?: (text: string) => void;
    onAssistantText?: (text: string) => void;
    onTurnEnd?: (turn: { text: string; interrupted: boolean }) => void;
    onTool?: (name: string) => void;
    onError?: (message: string) => void;
    onEnded?: (reason: KoridorEndReason) => void;
  }): Promise<KoridorCall>;
}

declare global {
  interface Window {
    KoridorVoice?: KoridorVoiceApi;
    webkitAudioContext?: typeof AudioContext;
  }
}

let clientLoading: Promise<KoridorVoiceApi> | null = null;

/** Loads Koridor's client once per page load; a failed load can be retried. */
export function loadKoridorVoice(): Promise<KoridorVoiceApi> {
  if (window.KoridorVoice) return Promise.resolve(window.KoridorVoice);
  if (!clientLoading) {
    clientLoading = new Promise<KoridorVoiceApi>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = KORIDOR_VOICE_CLIENT;
      script.async = true;
      script.onload = () => window.KoridorVoice
        ? resolve(window.KoridorVoice)
        : reject(new Error('Voice client did not initialise'));
      script.onerror = () => reject(new Error('Voice client failed to load'));
      document.head.appendChild(script);
    }).catch((err) => {
      clientLoading = null;
      throw err;
    });
  }
  return clientLoading;
}

/**
 * Routes the call's playback through `avatar` so the mouth follows the audio as it reaches the
 * speakers. Koridor's client connects every clip to its context's destination and exposes no
 * output node, so that context's destination is pointed at a pass-through node instead. Must run
 * before the first audio frame — i.e. straight after start() resolves. False when the client no
 * longer looks the way this expects; the caller then mouths along to the text.
 */
export function tapCallPlayback(call: KoridorCall, avatar: TalkingAvatar): boolean {
  const ctx = (call as { playCtx?: unknown }).playCtx;
  if (!(ctx instanceof (window.AudioContext || window.webkitAudioContext))) return false;
  try {
    const speakers = ctx.destination;
    const tap = ctx.createGain();
    Object.defineProperty(ctx, 'destination', { value: tap, configurable: true });
    if ((ctx.destination as AudioNode) !== tap) return false;
    avatar.attachNode(ctx, tap, speakers);
    return true;
  } catch {
    return false;
  }
}

export interface StartVoiceCallResponse {
  success: boolean;
  callId?: string;
  ticket?: string;
  socketUrl?: string;
  error?: string;
  /** Photos sent during the call reach it (hotel switch). */
  photosEnabled?: boolean;
}

export const UNAVAILABLE = "Voice calls aren't available right now.";

export const END_MESSAGES: Partial<Record<KoridorEndReason, string>> = {
  disconnected: 'The call was disconnected.',
  timeout: 'The call reached its time limit.',
  refused: UNAVAILABLE,
  error: 'The call ended because of a problem. Please try again.',
};

